import {dataTestId} from '@hconnect/uikit'
import {ControlledInput, Icon} from '@hm/fundamental'
import {mdiChevronDown, mdiChevronRight, mdiMagnify} from '@mdi/js'
import {Check} from '@mui/icons-material'
import {Box, Button, Stack} from '@mui/material'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {usePermission} from '../../permissions'
import {useUrlParam} from '../../routing'
import {useManageSamplingPointSections} from '../hooks/useManageSamplingPointSections'
import {useSamplingPointsManagement} from '../hooks/useSamplingPointsManagement'
import {
  LimsSamplingPoint,
  EquipmentOption,
  SamplingPointTypeOption,
  UpmDepartment,
  SamplingPointsCountsMap
} from '../types'

import {SamplingPointsSettingsTable} from './SamplingPointsSettingsTable/SamplingPointsSettingsTable'

interface SamplingPointsFormsProps {
  limsSamplingPoints: LimsSamplingPoint[]
  samplingPointTypeOptions: SamplingPointTypeOption[]
  assetOptions: EquipmentOption[]
  upmDepartmentOptions: UpmDepartment[]
  grindingCircuitOptions: EquipmentOption[]
  storageOptions: EquipmentOption[]
  samplingPointsCountsMap: SamplingPointsCountsMap
}

export const SamplingPointsSettingsContent: React.FC<SamplingPointsFormsProps> = ({
  limsSamplingPoints,
  samplingPointTypeOptions,
  assetOptions,
  upmDepartmentOptions,
  grindingCircuitOptions,
  storageOptions,
  samplingPointsCountsMap
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const canChangeSamplingPoints = usePermission('CHANGE_MATERIALS')
  const [searchTerm, setSearchTerm] = useState('')
  const [isPinnedSectionOpen, setIsPinnedSectionOpen] = useState(true)
  const [isAllSectionOpen, setIsAllSectionOpen] = useState(true)
  const [isLessUsedSectionOpen, setIsLessUsedSectionOpen] = useState(true)

  const {
    pinnedSamplingPointIds,
    lessUsedSamplingPointIds,
    handleTogglePinnedSamplingPoint,
    handleToggleLessUsedSamplingPoint
  } = useManageSamplingPointSections(plantCode)

  const {
    getRowData,
    getOriginalRowData,
    updateRowData,
    handleSamplingPointsMapping,
    isSavingSamplingPoints,
    hasChanges
  } = useSamplingPointsManagement(limsSamplingPoints)

  const availableSamplingPointIds = useMemo(
    () => new Set(limsSamplingPoints.map(({id}) => id)),
    [limsSamplingPoints]
  )

  const validPinnedSamplingPointIds = useMemo(() => {
    return new Set(
      [...pinnedSamplingPointIds].filter((samplingPointId) =>
        availableSamplingPointIds.has(samplingPointId)
      )
    )
  }, [pinnedSamplingPointIds, availableSamplingPointIds])

  const validLessUsedSamplingPointIds = useMemo(() => {
    return new Set(
      [...lessUsedSamplingPointIds].filter(
        (samplingPointId) =>
          availableSamplingPointIds.has(samplingPointId) &&
          !validPinnedSamplingPointIds.has(samplingPointId)
      )
    )
  }, [lessUsedSamplingPointIds, availableSamplingPointIds, validPinnedSamplingPointIds])

  const filteredLimsSamplingPoints = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase()

    if (!normalizedSearchTerm) {
      return limsSamplingPoints
    }

    return limsSamplingPoints.filter((limsSamplingPoint) =>
      [limsSamplingPoint.code, limsSamplingPoint.description ?? '']
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearchTerm)
    )
  }, [limsSamplingPoints, searchTerm])

  const pinnedSamplingPoints = useMemo(
    () =>
      filteredLimsSamplingPoints.filter((limsSamplingPoint) =>
        validPinnedSamplingPointIds.has(limsSamplingPoint.id)
      ),
    [filteredLimsSamplingPoints, validPinnedSamplingPointIds]
  )

  const lessUsedSamplingPoints = useMemo(
    () =>
      filteredLimsSamplingPoints.filter((limsSamplingPoint) =>
        validLessUsedSamplingPointIds.has(limsSamplingPoint.id)
      ),
    [filteredLimsSamplingPoints, validLessUsedSamplingPointIds]
  )

  const remainingSamplingPoints = useMemo(
    () =>
      filteredLimsSamplingPoints.filter(
        (limsSamplingPoint) =>
          !validPinnedSamplingPointIds.has(limsSamplingPoint.id) &&
          !validLessUsedSamplingPointIds.has(limsSamplingPoint.id)
      ),
    [filteredLimsSamplingPoints, validPinnedSamplingPointIds, validLessUsedSamplingPointIds]
  )

  return (
    <Stack spacing={3} sx={{minHeight: 0, minWidth: 0, overflowX: 'hidden'}}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          p: 1
        }}
      >
        <Box
          sx={{fontWeight: 600, fontSize: 20, order: 1}}
          {...dataTestId('sampling_points_settings_title')}
        >
          {t('samplingPointsSettings.samplingPoints')}
        </Box>
        <Box
          sx={{
            order: {xs: 3, md: 2},
            flexBasis: {xs: '100%', md: 'auto'},
            width: {xs: '100%', md: 520},
            maxWidth: '100%',
            mx: {xs: 'auto', md: 0},
            '& input': {
              minHeight: 32
            }
          }}
        >
          <ControlledInput
            name="samplingPointsSearch"
            value={searchTerm}
            icon={mdiMagnify}
            placeholder={t('samplingPointsSettings.searchByCode')}
            onChange={(event) => setSearchTerm(event.target.value)}
            data-cy="sampling_points_settings_search_input"
          />
        </Box>
        <Button
          color="primary"
          variant="contained"
          startIcon={<Check />}
          disabled={!canChangeSamplingPoints || !hasChanges}
          onClick={handleSamplingPointsMapping}
          sx={{order: {xs: 2, md: 3}, ml: 'auto'}}
          {...dataTestId('sampling_points_settings_save_button')}
        >
          {t('common.save')}
        </Button>
      </Box>
      {filteredLimsSamplingPoints.length > 0 ? (
        <>
          {pinnedSamplingPoints.length > 0 && (
            <details
              open={isPinnedSectionOpen}
              onToggle={(event) => setIsPinnedSectionOpen(event.currentTarget.open)}
              className="w-full"
              {...dataTestId('sampling_points_settings_pinned_section')}
            >
              <summary className="flex cursor-pointer items-center gap-2 py-1 text-base font-semibold">
                <Icon
                  path={isPinnedSectionOpen ? mdiChevronDown : mdiChevronRight}
                  size="regular"
                />
                {t('samplingPointsSettings.pinnedRows')}
              </summary>
              <div className="mt-2 w-full max-w-full min-h-0 min-w-0 overflow-x-auto overflow-y-hidden">
                <SamplingPointsSettingsTable
                  limsSamplingPoints={pinnedSamplingPoints}
                  samplingPointTypeOptions={samplingPointTypeOptions}
                  assetOptions={assetOptions}
                  isSaving={isSavingSamplingPoints}
                  upmDepartmentOptions={upmDepartmentOptions}
                  grindingCircuitOptions={grindingCircuitOptions}
                  storageOptions={storageOptions}
                  getRowData={getRowData}
                  getOriginalRowData={getOriginalRowData}
                  updateRowData={updateRowData}
                  samplingPointsCountsMap={samplingPointsCountsMap}
                  pinnedSamplingPointIds={validPinnedSamplingPointIds}
                  onTogglePinnedSamplingPoint={handleTogglePinnedSamplingPoint}
                  lessUsedSamplingPointIds={validLessUsedSamplingPointIds}
                  onToggleLessUsedSamplingPoint={handleToggleLessUsedSamplingPoint}
                  showMobileSorting={true}
                />
              </div>
            </details>
          )}
          {remainingSamplingPoints.length > 0 && (
            <details
              open={isAllSectionOpen}
              onToggle={(event) => setIsAllSectionOpen(event.currentTarget.open)}
              className="w-full"
              {...dataTestId('sampling_points_settings_all_section')}
            >
              <summary className="flex cursor-pointer items-center gap-2 py-1 text-base font-semibold">
                <Icon path={isAllSectionOpen ? mdiChevronDown : mdiChevronRight} size="regular" />
                {t('samplingPointsSettings.allSamplingPoints')}
              </summary>
              <div className="mt-2 w-full max-w-full min-h-0 min-w-0 overflow-x-auto overflow-y-hidden">
                <SamplingPointsSettingsTable
                  limsSamplingPoints={remainingSamplingPoints}
                  samplingPointTypeOptions={samplingPointTypeOptions}
                  assetOptions={assetOptions}
                  isSaving={isSavingSamplingPoints}
                  upmDepartmentOptions={upmDepartmentOptions}
                  grindingCircuitOptions={grindingCircuitOptions}
                  storageOptions={storageOptions}
                  getRowData={getRowData}
                  getOriginalRowData={getOriginalRowData}
                  updateRowData={updateRowData}
                  samplingPointsCountsMap={samplingPointsCountsMap}
                  pinnedSamplingPointIds={validPinnedSamplingPointIds}
                  onTogglePinnedSamplingPoint={handleTogglePinnedSamplingPoint}
                  lessUsedSamplingPointIds={validLessUsedSamplingPointIds}
                  onToggleLessUsedSamplingPoint={handleToggleLessUsedSamplingPoint}
                  showMobileSorting={true}
                />
              </div>
            </details>
          )}
          {lessUsedSamplingPoints.length > 0 && (
            <details
              open={isLessUsedSectionOpen}
              onToggle={(event) => setIsLessUsedSectionOpen(event.currentTarget.open)}
              className="w-full"
              {...dataTestId('sampling_points_settings_less_used_section')}
            >
              <summary className="flex cursor-pointer items-center gap-2 py-1 text-base font-semibold">
                <Icon
                  path={isLessUsedSectionOpen ? mdiChevronDown : mdiChevronRight}
                  size="regular"
                />
                {t('samplingPointsSettings.lessUsedRows')}
              </summary>
              <div className="mt-2 w-full max-w-full min-h-0 min-w-0 overflow-x-auto overflow-y-hidden">
                <SamplingPointsSettingsTable
                  limsSamplingPoints={lessUsedSamplingPoints}
                  samplingPointTypeOptions={samplingPointTypeOptions}
                  assetOptions={assetOptions}
                  isSaving={isSavingSamplingPoints}
                  upmDepartmentOptions={upmDepartmentOptions}
                  grindingCircuitOptions={grindingCircuitOptions}
                  storageOptions={storageOptions}
                  getRowData={getRowData}
                  getOriginalRowData={getOriginalRowData}
                  updateRowData={updateRowData}
                  samplingPointsCountsMap={samplingPointsCountsMap}
                  pinnedSamplingPointIds={validPinnedSamplingPointIds}
                  onTogglePinnedSamplingPoint={handleTogglePinnedSamplingPoint}
                  lessUsedSamplingPointIds={validLessUsedSamplingPointIds}
                  onToggleLessUsedSamplingPoint={handleToggleLessUsedSamplingPoint}
                  showMobileSorting={true}
                />
              </div>
            </details>
          )}
        </>
      ) : (
        <Box
          sx={{width: '100%', minHeight: 0, minWidth: 0, textAlign: 'center'}}
          {...dataTestId('sampling_points_settings_no_results')}
        >
          {t('samplingPointsSettings.noResultsFound')}
        </Box>
      )}
    </Stack>
  )
}
